import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { SEVERITY_STYLE } from "./AnomalyPanel";

export default function AlertsBell() {
  const [alerts, setAlerts] = useState([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);

  const load = () => platformApi.get("/anomaly/alerts?status=active")
    .then((r) => { setAlerts(r.data.alerts); setActive(r.data.active_count); })
    .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // gentle poll
    return () => clearInterval(t);
  }, []);

  const acknowledge = async (id) => {
    setAckBusy(id);
    try {
      await platformApi.post(`/anomaly/alerts/${id}/acknowledge`);
      toast.success("Alert acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAckBusy(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative text-slate-400 hover:text-white transition-colors" data-testid="platform-alerts-bell">
          <Bell className="h-4 w-4" />
          {active > 0 && (
            <span data-testid="platform-alerts-count"
              className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 flex items-center justify-center text-[9px] font-mono font-bold text-white bg-red-500 rounded-full">
              {active}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-none w-96 p-0" data-testid="platform-alerts-popover">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1E293B]">
            Anomaly alerts {active > 0 ? `· ${active} active` : ""}
          </p>
        </div>
        {alerts.length === 0 ? (
          <p className="px-4 py-6 text-[12px] text-slate-400 flex items-center gap-2" data-testid="platform-alerts-empty">
            <CheckCircle2 className="h-4 w-4 text-teal-600" /> No active alerts.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {alerts.map((a) => (
              <div key={a.id} className="px-4 py-2.5" data-testid={`bell-alert-${a.id}`}>
                <div className="flex items-start gap-2">
                  <span className={`text-[8px] font-mono uppercase px-1 py-0.5 border shrink-0 mt-0.5 ${SEVERITY_STYLE[a.severity] || ""}`}>
                    {a.severity}
                  </span>
                  <p className="text-[11.5px] text-[#1E293B] leading-snug flex-1">{a.summary}</p>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[9px] font-mono text-slate-400">
                    {a.tenant_id} · {new Date(a.created_at).toLocaleString("en-GB")}
                  </span>
                  <button onClick={() => acknowledge(a.id)} disabled={ackBusy === a.id}
                    data-testid={`bell-ack-${a.id}`}
                    className="text-[9px] font-mono uppercase text-teal-700 hover:underline disabled:opacity-50 flex items-center gap-1">
                    {ackBusy === a.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : null} dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
